import {
  BaseQueryApi,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import NProgress from "nprogress";
import "nprogress/nprogress.css";

const hostDomain = `${process.env.REACT_APP_API_URL}/api`;

export type QueryReturnValue<T = unknown, E = unknown, M = unknown> =
  | {
      error: E;
      data?: undefined;
      meta?: M;
    }
  | {
      error?: undefined;
      data: T;
      meta?: M;
    };

type TypeAuthorization = {
  email: string;
  password: string;
};

export interface AuthResponse {
  success: boolean;
  token: string;
}

export interface Response<DataType> {
  success: boolean;
  data: DataType;
}

const nprogress = {
  counter: 0,
  start: function () {
    this.counter++;
    !NProgress.isStarted() && NProgress.start();
  },
  complete: function () {
    this.counter--;
    this.counter === 0 && NProgress.done();
  },
};

// const baseQueryWithNProgress: BaseQueryFn<
//   string | FetchArgs,
//   unknown,
//   FetchBaseQueryError
// > = async (args, api, extraOptions) => {
//   try {
//     nprogress.start();
//     const result = await baseQuery(args, api, extraOptions);

//     nprogress.complete();
//     return result;
//   } catch (err) {
//     nprogress.complete();
//     throw err;
//   }
// };

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: {}
): Promise<any> => {
  const baseQuery = fetchBaseQuery({
    baseUrl: `${hostDomain}`,
    prepareHeaders: (headers) => {
      const token = localStorage.access_token;

      headers.set("authorization", `Bearer ${token}`);
      headers.set("Accept", "application/json");

      return headers;
    },
  });
  nprogress.start();
  const result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status !== 401) {
    nprogress.complete();
    return result;
  }

  const forceLogout = () => {
    nprogress.complete();
    localStorage.clear();
    window.location.replace("/login");
  };

  const token = localStorage.access_token;
  if (!token) {
    return forceLogout();
  }

  const refreshResult: QueryReturnValue<
    any,
    FetchBaseQueryError,
    FetchBaseQueryMeta
  > = await baseQuery(
    {
      url: "refresh/",
      method: "POST",
      body: {
        refresh: token,
      },
    },
    api,
    extraOptions
  );

  if (
    refreshResult?.error?.status === 401 ||
    refreshResult?.error?.status === 422
  ) {
    return forceLogout();
  }

  if (refreshResult.data) {
    const newToken = refreshResult.data.data.access_token;
    localStorage.setItem("access_token", newToken);
    const user = {
      ...JSON.parse(localStorage.getItem("auth") || "null"),
      token: newToken,
    };
    localStorage.setItem("auth", JSON.stringify(user));
  }

  const retryResult = await baseQuery(args, api, extraOptions);

  if (retryResult?.error?.status === 401) {
    return forceLogout();
  }
  nprogress.complete();
  return retryResult;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ApplicationList", "ApplicationBank"],
  endpoints: (builder) => ({
    agentAuth: builder.mutation<AuthResponse, TypeAuthorization>({
      query(data) {
        return {
          url: `/login`,
          method: "POST",
          body: data,
        };
      },
    }),
  }),
});

export const { useAgentAuthMutation } = api;
